/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Mon May 19 2025
 **/

import React, { ComponentType, useEffect, useState } from "react";

export function wrappedPromise<T>(promise: Promise<T>) {
	let status: "pending" | "success" | "error" = "pending";
	let result: T | undefined = undefined;

	const suspender = promise
		.then((data: T) => {
			status = "success";
			result = data;
		})
		.catch((error) => {
			status = "error";
			result = error;
		});

	return {
		suspenseRead() {
			if (status === "pending") {
				throw suspender;
			} else if (status === "error") {
				throw result;
			} else if (status === "success") {
				return result;
			}
		}
	};
}

export function withProcedure<P extends React.PropsWithChildren>(
	WrappedComponent: ComponentType<P>,
	procedure: () => ReturnType<typeof wrappedPromise>
) {
	const resource = () => procedure;
	return function (props: P) {
		const [a, setA] = useState<ReturnType<ReturnType<typeof resource>> | null>(null);
		useEffect(() => {
			setA(resource());
		}, []);

		const data = a?.suspenseRead();
		if (data === undefined) return;
		return <WrappedComponent {...props} />;
	};
}
